import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { UmsService } from '../ums.service';
import { ModalpopService } from '../modalpop.service';
import { ReportService } from '../report.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-college-marksheet-ledger',
  templateUrl: './college-marksheet-ledger.component.html',
  styleUrls: ['./college-marksheet-ledger.component.scss']
})
export class CollegeMarksheetLedgerComponent implements OnInit {

  ledgerForm: FormGroup;
  dialogRef: MatDialogRef<any>;
  APIService: string;

  TitleOne = 'CollegeWise Result';
  RGInCode: any = '';
  SessionNo: any = '';

  readonly LEDGER_RPT_CODE = 19;

  // Lists
  sessionList: any[] = [];
  filteredSessionList: any[] = [];
  examList: any[] = [];
  filteredListExam: any[] = [];
  courseList: any[] = [];
  filteredListCourse: any[] = [];
  paperList: any[] = [];
  filteredListPaper: any[] = [];
  collegeList: any[] = [];
  filteredCollegeList: any[] = [];

  // College multi-select state (mirrors IA Report)
  selectedColleges: string[] = [];
  commaSeparatedColleges: string = '';

  userPermission: any;
  isCollegeUser = false;
  UserCollege = '';

  isDownloading = false;
  showDownloadButton = false;

  constructor(
    public umsService: UmsService,
    private spinner: NgxSpinnerService,
    private modalPopupService: ModalpopService,
    public fb: FormBuilder,
    private route: ActivatedRoute,
    public router: Router,
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private reportService: ReportService
  ) {
    this.APIService = environment.serviceUrl;
  }

  ngOnInit(): void {
    try {
      this.userPermission = JSON.parse(sessionStorage.getItem('userPermission') || '[]');
    } catch {
      this.userPermission = [];
    }

    if (this.userPermission?.length > 0 && this.userPermission[0].CgUi === 'C') {
      this.isCollegeUser = true;
      this.UserCollege = this.userPermission[0].CgId;
    }

    this.ledgerForm = this.fb.group({
      Session: new FormControl('', Validators.required),
      Exam:    new FormControl('', Validators.required),
      Course:  new FormControl(''),
      College: new FormControl([]),      // <-- now an array (multi-select)
      Paper:   new FormControl(''),
      Name1:        new FormControl(''),
      Designation1: new FormControl(''),
      Name2:        new FormControl(''),
      Designation2: new FormControl(''),
      Name3:        new FormControl(''),
      Designation3: new FormControl(''),
      Name4:        new FormControl(''),
      Designation4: new FormControl(''),
      Name5:        new FormControl(''),
      Designation5: new FormControl(''),
      Name6:        new FormControl(''),
      Designation6: new FormControl('')
    });

    // If college user, pre-lock their college
    if (this.isCollegeUser && this.UserCollege) {
      this.selectedColleges = [this.UserCollege];
      this.commaSeparatedColleges = this.UserCollege;
      this.ledgerForm.controls['College'].setValue([this.UserCollege]);
    }

    this.ledgerForm.valueChanges.subscribe(() => this.updateDownloadButtonVisibility());

    const sessionData = this.safeParse(sessionStorage.getItem('session'));
    if (sessionData && sessionData.SnNo) {
      this.SessionNo = sessionData.SnNo;
      this.ledgerForm.controls['Session'].setValue(this.SessionNo);
    } else {
      this.umsService.getSecretData().subscribe({
        next: (res) => {
          if (res?.projectPharam?.['SnNo']) {
            this.SessionNo = res.projectPharam['SnNo'];
            this.ledgerForm.controls['Session'].setValue(this.SessionNo);
            this.fetchExam();
          }
        },
        error: () => {}
      });
    }

    this.route.queryParams.subscribe((params) => {
      this.RGInCode = params['MenuCode'] || '';
    });

    this.fetchSessionList();

    if (this.SessionNo) {
      this.fetchExam();
    }
  }

  private safeParse(value: string | null): any {
    if (!value) return null;
    try { return JSON.parse(value); } catch { return null; }
  }

  private updateDownloadButtonVisibility(): void {
    const v = this.ledgerForm.value;
    this.showDownloadButton = !!(v.Session && v.Exam);
  }

  // ─── Session ──────────────────────────────────────────────────
  fetchSessionList() {
    this.spinner.show();
    this.umsService.getSession({ SnActive: 'Y' }).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.sessionList = (res && res.status && Array.isArray(res.data)) ? res.data : [];
        this.filteredSessionList = this.sessionList.slice();
      },
      error: () => {
        this.spinner.hide();
        this.sessionList = [];
        this.filteredSessionList = [];
      }
    });
  }

  // ─── Exam (on Session change) ─────────────────────────────────
  fetchExam() {
    if (!this.SessionNo) {
      this.examList = [];
      this.filteredListExam = [];
      return;
    }
    this.spinner.show();
    const payload = {
      LoginId: sessionStorage.getItem('userId'),
      ShSnNo: this.SessionNo,
      ShStatus: 'OARU'
    };
    this.umsService.ReportExm(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.examList = (res && res.status && Array.isArray(res.data)) ? res.data : [];
        this.filteredListExam = this.examList.slice();
      },
      error: () => {
        this.spinner.hide();
        this.examList = [];
        this.filteredListExam = [];
      }
    });
  }

  // ─── Course (on Exam change) ──────────────────────────────────
  fetchCourse(ShId: any) {
    if (!ShId) {
      this.courseList = [];
      this.filteredListCourse = [];
      return;
    }
    this.spinner.show();
    const payload = {
      LoginId: parseInt(sessionStorage.getItem('userId')) || 0,
      ShId: ShId,
      CgCode: this.UserCollege || ''
    };
    this.umsService.getCourse(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.courseList = (res && res.status && Array.isArray(res.data)) ? res.data : [];
        this.filteredListCourse = this.courseList.slice();
      },
      error: () => {
        this.spinner.hide();
        this.courseList = [];
        this.filteredListCourse = [];
      }
    });
  }

  // ─── Papers (kept for backward-compat, hidden in UI) ──────────
  fetchPaper(ShId: string, ColUni: string = 'U') {
    if (!ShId) {
      this.paperList = [];
      this.filteredListPaper = [];
      return;
    }
    this.spinner.show();
    const payload = { ShId, EmId: 0, EsCollUni: ColUni };
    this.umsService.fetchHOM(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.paperList = (res && res.status && Array.isArray(res.data)) ? res.data : [];
        this.filteredListPaper = this.paperList.slice();
      },
      error: () => {
        this.spinner.hide();
        this.paperList = [];
        this.filteredListPaper = [];
      }
    });
  }

  // ─── Colleges (NEW — on Course change, like IA Report) ────────
  fetchColleges() {
    const courseCode = this.ledgerForm.get('Course').value;

    if (!courseCode) {
      this.collegeList = [];
      this.filteredCollegeList = [];
      this.selectedColleges = [];
      this.commaSeparatedColleges = '';
      this.ledgerForm.controls['College'].setValue([]);
      return;
    }

    this.spinner.show();
    const payload = { CoCode: courseCode };

    this.umsService.fetchstucourseClgMap(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        if (res.status && res.data) {
          this.collegeList = res.data;

          // Restrict to user college if applicable
          if (this.UserCollege) {
            this.collegeList = this.collegeList.filter(
              (c: any) => c.CgCode === this.UserCollege
            );
          }

          this.filteredCollegeList = this.collegeList.slice();

          // Auto-select when only one college available
          if (this.collegeList.length === 1) {
            this.selectedColleges = [this.collegeList[0].CgCode];
            this.commaSeparatedColleges = this.selectedColleges.join(',');
            this.ledgerForm.controls['College'].setValue(this.selectedColleges);
          } else {
            this.selectedColleges = [];
            this.commaSeparatedColleges = '';
            this.ledgerForm.controls['College'].setValue([]);
          }
        } else {
          this.collegeList = [];
          this.filteredCollegeList = [];
          this.selectedColleges = [];
          this.commaSeparatedColleges = '';
          this.ledgerForm.controls['College'].setValue([]);
        }
      },
      error: () => {
        this.spinner.hide();
        this.collegeList = [];
        this.filteredCollegeList = [];
        this.selectedColleges = [];
        this.commaSeparatedColleges = '';
        this.ledgerForm.controls['College'].setValue([]);
      }
    });
  }

  // ─── Handlers ────────────────────────────────────────────────
  onSessionChange(sessionId: any) {
    if (!sessionId) return;
    this.SessionNo = sessionId;
    this.ledgerForm.controls['Session'].setValue(sessionId);

    this.ledgerForm.patchValue({ Exam: '', Course: '', College: [] });

    this.examList = [];
    this.filteredListExam = [];
    this.courseList = [];
    this.filteredListCourse = [];
    this.collegeList = [];
    this.filteredCollegeList = [];
    this.selectedColleges = [];
    this.commaSeparatedColleges = '';

    this.fetchExam();
  }

  onExamChange(exm: any) {
    if (!exm || !exm.ShId) {
      this.courseList = [];
      this.filteredListCourse = [];
      this.collegeList = [];
      this.filteredCollegeList = [];
      return;
    }

    this.ledgerForm.controls['Exam'].setValue(exm.ShId);
    this.ledgerForm.patchValue({ Course: '', College: [] });

    this.selectedColleges = [];
    this.commaSeparatedColleges = '';
    this.collegeList = [];
    this.filteredCollegeList = [];

    this.fetchPaper(exm.ShId, 'U');
    this.fetchCourse(exm.ShId);
  }

  onCourseChange(courseCode: any) {
    this.ledgerForm.controls['Course'].setValue(courseCode);
    // reset college selection
    this.selectedColleges = [];
    this.commaSeparatedColleges = '';
    this.ledgerForm.controls['College'].setValue([]);
    this.fetchColleges();
  }

  onCollegeSelect(event: any): void {
    let selected = event.value ? [...event.value] : [];
    selected = selected.filter((v: any) => v !== 'selectAll');

    this.selectedColleges = selected;
    this.ledgerForm.controls['College'].setValue(selected, { emitEvent: false });
    this.commaSeparatedColleges = this.selectedColleges.join(',');
    this.updateDownloadButtonVisibility();
  }

  selectAllColleges(): void {
    if (this.isAllSelected()) {
      this.selectedColleges = [];
      this.ledgerForm.controls['College'].setValue([], { emitEvent: false });
      this.commaSeparatedColleges = '';
    } else {
      this.selectedColleges = this.filteredCollegeList.map(c => c.CgCode);
      this.ledgerForm.controls['College'].setValue(this.selectedColleges, { emitEvent: false });
      this.commaSeparatedColleges = this.selectedColleges.join(',');
    }
    this.updateDownloadButtonVisibility();
  }

  isAllSelected(): boolean {
    return this.selectedColleges.length === this.filteredCollegeList.length
        && this.filteredCollegeList.length > 0;
  }

  isIndeterminate(): boolean {
    return this.selectedColleges.length > 0
        && this.selectedColleges.length < this.filteredCollegeList.length;
  }

  // ─── Print ───────────────────────────────────────────────────
   onPrintLedger() {
    if (this.ledgerForm.invalid) {
      this.ledgerForm.get('Exam').markAsTouched();
      return;
    }

    this.isDownloading = true;
    const v = this.ledgerForm.value;

    // Comma-separated college codes
    const collegeValue = this.isCollegeUser
      ? this.UserCollege
      : (this.commaSeparatedColleges ||
         (Array.isArray(v.College) ? v.College.join(',') : (v.College || '')));

    const strIdValue = Number((v as any).StrId) || 0;

    const payload1: any = {
      ShId:         v.Exam ? Number(v.Exam) : 0,
      CgCode:       collegeValue || '',
      // CoCode:       v.Course || '',
      // SnNo:         this.SessionNo || '',
      StrId:        strIdValue,           
      Name1:        v.Name1        || null,
      Name2:        v.Name2        || null,
      Name3:        v.Name3        || null,
      Name4:        v.Name4        || null,
      Name5:        v.Name5        || null,
      Name6:        v.Name6        || null,
      Designation1: v.Designation1 || null,
      Designation2: v.Designation2 || null,
      Designation3: v.Designation3 || null,
      Designation4: v.Designation4 || null,
      Designation5: v.Designation5 || null,
      Designation6: v.Designation6 || null
    };

    const jsonString = JSON.stringify(payload1);

    const filter: any = {
      LoginId: parseInt(sessionStorage.getItem('userId')) || 0,
      RptCode: 184,
      InstName: '',
      InstNo: this.umsService.secretdata['ClubPartTrans'] || 0,
      Extra1: jsonString,
      ShId: v.Exam || '',
      CgCode: collegeValue || '',
      RollNo: 0,
      Cent: '',
      TnCode: '',
      EsId: v.Paper || 0,
      CsNo: '',
      PgBreak: '',
      StrId: strIdValue,                    // ★ stays in sync with Extra1
      StrCode: '',
      RgNo: '',
      UC: '',
      Ledger: '',
      RegnRoll: '',
      DsCode: '',
      CoCode: v.Course || '',
      SnNo: this.SessionNo,
      Mode: true
    };

    this.reportService.generateHbsReport(filter);

    setTimeout(() => { this.isDownloading = false; }, 1000);
  }

  back() {
    this.router.navigate(['/']);
  }
}